import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, ClipboardCheck, XCircle } from 'lucide-react';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Textarea from '../components/forms/Textarea';
import Table from '../components/tables/Table';
import { driverService, vehicleService } from '../services';

const approvalBadge = (value) => (
  <Badge variant={value === 'APPROVED' ? 'success' : value === 'REJECTED' ? 'danger' : 'warning'}>
    {value === 'APPROVED' ? 'Aprobado' : value === 'REJECTED' ? 'Rechazado' : 'Pendiente'}
  </Badge>
);

const driverApproval = (value, item) => (
  <div className="space-y-1">
    {approvalBadge(value)}
    {item.approved_with_exception && (
      <Badge variant="warning">Con excepción</Badge>
    )}
  </div>
);

const vehicleApproval = (value, item) => (
  <div className="space-y-1">
    {approvalBadge(value)}
    {item.approved_with_exception && <Badge variant="warning">Con excepción</Badge>}
  </div>
);

function DocumentPreview({ label, url }) {
  return <div>
    <p className="mb-1 text-xs font-medium text-content-muted">{label}</p>
    {url ? <a href={url} target="_blank" rel="noreferrer" className="block rounded-control border p-2 hover:border-primary-300"><img src={url} alt={label} className="h-40 w-full rounded-md bg-slate-50 object-contain" /><span className="mt-2 block text-center text-xs font-medium text-primary-700">Abrir imagen completa</span></a> : <Alert variant="warning" title="Documento no disponible">Registro anterior sin imagen cargada.</Alert>}
  </div>;
}

function FleetReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [review, setReview] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [summaryResponse, driverResponse, vehicleResponse] = await Promise.all([
        vehicleService.getReviewSummary(), driverService.getAll({ page_size: 100 }),
        vehicleService.getAll({ page_size: 100 }),
      ]);
      setSummary(summaryResponse.data.results || []);
      setDrivers(driverResponse.data);
      setVehicles(vehicleResponse.data);
    } catch (requestError) {
      setError(requestError.message || 'No fue posible cargar la revisión de flota.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const requestedVehicleId = searchParams.get('vehicle_id');
    if (!requestedVehicleId || loading) return;
    const vehicle = vehicles.find((item) => String(item.id) === requestedVehicleId);
    if (vehicle) {
      setReview({ type: 'vehicle', item: vehicle });
      setNote(vehicle.review_note || '');
    } else {
      setError('El móvil solicitado no existe o está fuera de tu estructura.');
    }
    setSearchParams({}, { replace: true });
  }, [loading, searchParams, setSearchParams, vehicles]);

  const decide = async (decision) => {
    const hasMissingRequirements = (review?.item.missing_requirements || []).length > 0;
    if ((decision === 'REJECTED' || (decision === 'APPROVED' && hasMissingRequirements)) && !note.trim()) {
      setError(decision === 'REJECTED'
        ? 'Debes indicar el motivo del rechazo.'
        : 'Debes agregar una observación para aprobar este registro por excepción.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (review.type === 'driver') await driverService.review(review.item.id, decision, note);
      else await vehicleService.review(review.item.id, decision, note);
      setReview(null);
      setNote('');
      setNotice(decision === 'APPROVED' ? 'Registro aprobado correctamente.' : 'Registro rechazado con observación.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'No fue posible registrar la decisión.');
    } finally {
      setSaving(false);
    }
  };

  const actions = (type, item) => (
    <Button variant="outline" size="sm" onClick={() => { setReview({ type, item }); setNote(item.review_note || ''); }}>
      <ClipboardCheck className="size-4" /> Revisar
    </Button>
  );
  const summaryColumns = useMemo(() => [
    { key: 'coordinator_name', header: 'Coordinador' },
    { key: 'leaders', header: 'Punteros' }, { key: 'voters', header: 'Votantes' },
    { key: 'vehicles', header: 'Móviles presentados' },
    { key: 'capacity', header: 'Capacidad total' },
    { key: 'voters_per_vehicle', header: 'Votantes por móvil' },
    { key: 'voters_per_leader', header: 'Votantes por puntero' },
    { key: 'pending_vehicles', header: 'Móviles pendientes' },
    { key: 'pending_drivers', header: 'Conductores pendientes' },
  ], []);

  if (loading) return <div className="grid min-h-72 place-items-center"><LoadingSpinner label="Calculando necesidad de flota" /></div>;
  return <div className="space-y-6">
    <div><p className="text-sm font-semibold text-primary-700">Validación administrativa</p><h1 className="mt-1 text-2xl font-bold">Revisión de flota y conductores</h1><p className="mt-1 text-sm text-content-muted">Evalúa la cantidad presentada según punteros, votantes, capacidad y promedio por móvil.</p></div>
    {error && <Alert variant="danger" title="No se pudo completar" onClose={() => setError('')}>{error}</Alert>}
    {notice && <Alert variant="success" title="Decisión registrada" onClose={() => setNotice('')}>{notice}</Alert>}
    <Card title="Necesidad por coordinador" description="Ejemplo: 50 votantes y 10 móviles equivalen a un promedio de 5 votantes por móvil.">
      <Table columns={summaryColumns} data={summary} rowKey="coordinator_id" emptyMessage="No existen coordinadores para revisar." />
    </Card>
    <Card title="Conductores presentados" description="El coordinador los registra; aquí se aprueban o rechazan.">
      <Table data={drivers} columns={[
        { key: 'resolved_document', header: 'Cédula' }, { key: 'resolved_names', header: 'Conductor' },
        { key: 'coordinator_name', header: 'Responsable' },
        { key: 'person_type', header: 'Tipo' }, { key: 'license_number', header: 'Licencia' },
        { key: 'license_expiration', header: 'Vencimiento' },
        { key: 'approval_status', header: 'Revisión', render: driverApproval },
        { key: 'actions', header: 'Acción', render: (_, item) => actions('driver', item) },
      ]} emptyMessage="No hay conductores presentados." />
    </Card>
    <Card title="Móviles presentados" description="Un móvil solo puede aprobarse cuando su conductor principal ya fue aprobado.">
      <Table data={vehicles} columns={[
        { key: 'plate', header: 'Chapa' }, { key: 'vehicle_type', header: 'Tipo' },
        { key: 'coordinator_name', header: 'Responsable' },
        { key: 'driver_name', header: 'Conductor' }, { key: 'capacity', header: 'Capacidad' },
        { key: 'zone', header: 'Zona' }, { key: 'approval_status', header: 'Revisión', render: vehicleApproval },
        { key: 'actions', header: 'Acción', render: (_, item) => actions('vehicle', item) },
      ]} emptyMessage="No hay móviles presentados." />
    </Card>
    <Modal open={Boolean(review)} onClose={() => !saving && setReview(null)} size="lg" title={review?.type === 'driver' ? 'Revisar conductor' : 'Revisar móvil'} description={review?.type === 'driver' ? review?.item.resolved_names : review?.item.plate} footer={<><Button variant="danger" onClick={() => decide('REJECTED')} loading={saving}><XCircle className="size-4" /> Rechazar</Button><Button onClick={() => decide('APPROVED')} loading={saving}><CheckCircle2 className="size-4" /> {review?.item.missing_requirements?.length ? 'Aprobar con excepción' : 'Aprobar'}</Button></>}>
      <div className="space-y-4"><Alert variant="info" title="Criterio de revisión">Verifica licencia, conductor, capacidad y los documentos antes de decidir.</Alert>{review?.item.missing_requirements?.length > 0 && <Alert variant="warning" title="Requisitos incompletos">Puedes aprobar excepcionalmente, pero debes justificarlo en la observación. Pendiente: {review.item.missing_requirements.join(', ')}.</Alert>}{review?.type === 'driver' ? <DocumentPreview label="Frente del registro de conducir" url={review.item.license_front_image} /> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"><DocumentPreview label="Habilitación" url={review?.item.authorization_image} /><DocumentPreview label="Cédula verde · Frente" url={review?.item.green_card_front_image} /><DocumentPreview label="Cédula verde · Dorso" url={review?.item.green_card_back_image} /></div>}<Textarea id="fleet-review-note" label="Observación de revisión" required={review?.item.missing_requirements?.length > 0} value={note} onChange={(event) => setNote(event.target.value)} hint={review?.item.missing_requirements?.length > 0 ? 'Obligatoria para aprobar por excepción o rechazar.' : 'Obligatoria cuando se rechaza.'} /></div>
    </Modal>
  </div>;
}

export default FleetReviewPage;
